import { useTranslation } from 'react-i18next';
import { LogOut } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { useCurrentUser } from '@/shared/hooks/use-current-user';
import { cn } from '@/shared/lib/utils';

interface SidebarFooterProps {
  collapsed: boolean;
}

function initialsFor(user: { name?: string | null; email?: string | null }): string {
  const source = user.name?.trim() || user.email?.trim() || '';
  if (!source) return '·';
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const second = parts[1]?.[0] ?? '';
  const combined = (first + second).toUpperCase();
  return combined || source.charAt(0).toUpperCase();
}

export function SidebarFooter({ collapsed }: SidebarFooterProps) {
  const { t } = useTranslation('common');
  const { user, logout } = useCurrentUser();

  const displayName = user?.name ?? user?.email ?? t('labels.user');
  const role = user?.role?.toLowerCase() ?? '';

  return (
    <div
      className={cn(
        'border-t border-sidebar-border',
        collapsed ? 'p-2 flex justify-center' : 'px-3.5 py-3',
      )}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex items-center rounded-md transition-colors text-left',
              'hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/50',
              collapsed ? 'p-1' : 'gap-2.5 w-full p-1',
            )}
            aria-label={displayName}
          >
            <span
              className="grid place-items-center h-7 w-7 rounded-full text-[11px] font-bold shrink-0"
              style={{
                background: 'linear-gradient(135deg, #F5A623, #c07a14)',
                color: '#0b1220',
              }}
              aria-hidden="true"
            >
              {initialsFor(user ?? {})}
            </span>
            {!collapsed && (
              <span className="flex flex-col min-w-0">
                <span className="text-[12px] font-medium truncate text-sidebar-foreground">
                  {displayName}
                </span>
                {role && (
                  <span className="font-mono text-[9.5px] uppercase tracking-[0.5px] text-sidebar-foreground/55">
                    {role}
                  </span>
                )}
              </span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={collapsed ? 'end' : 'start'} side="top" className="w-56">
          <DropdownMenuLabel>
            <div>
              <div className="font-medium">{displayName}</div>
              {user?.email && <div className="text-xs text-muted-foreground">{user.email}</div>}
              {role && (
                <div className="text-xs text-muted-foreground capitalize mt-0.5">{role}</div>
              )}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive">
            <LogOut className="h-4 w-4 mr-2" />
            {t('buttons.signOut')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
